/**
 * PaymentCallbackPage — Telr redirect target
 * - If loaded inside iframe (card payment): sends postMessage to parent
 * - If loaded directly (mobile after QR scan): shows result screen + calls verify
 */
import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useCustomerAuth } from '../../context/CustomerAuthContext';
import { CheckCircle2, XCircle, AlertTriangle } from 'lucide-react';

export default function PaymentCallbackPage() {
  const [searchParams] = useSearchParams();
  const navigate       = useNavigate();
  const { apiCall }    = useCustomerAuth();
  const [state, setState] = useState('processing');

  const payment = searchParams.get('payment'); // auth | decl | can
  const reqId   = searchParams.get('reqid');

  useEffect(() => {
    const inIframe  = window.parent !== window;
    const inPopup   = !!(window.opener && !window.opener.closed && window.opener !== window);

    // ── Popup case (card payment) ──
    if (inPopup) {
      window.opener.postMessage(
        { type: 'telr_payment', status: payment, reqid: reqId },
        window.location.origin
      );
      setState(payment === 'auth' ? 'verifying' : payment === 'can' ? 'cancelled' : 'failed');
      // Close popup after short delay so user can see the state
      setTimeout(() => window.close(), 1200);
      return;
    }

    // ── iFrame case (legacy fallback) ──
    if (inIframe) {
      window.parent.postMessage(
        { type: 'telr_payment', status: payment, reqid: reqId },
        window.location.origin
      );
      setState(payment === 'auth' ? 'verifying' : payment === 'can' ? 'cancelled' : 'failed');
      return;
    }

    // ── Direct access on mobile (after QR scan) ──
    if (payment === 'can') {
      setState('cancelled');
      setTimeout(() => navigate('/customer/wallet', { replace: true }), 2500);
      return;
    }
    if (payment === 'decl') {
      setState('failed');
      setTimeout(() => navigate('/customer/wallet', { replace: true }), 2500);
      return;
    }
    if (payment === 'auth' && reqId) {
      apiCall(`/customer/payment/topup/verify?reqid=${encodeURIComponent(reqId)}`, { method: 'POST' })
        .then(res => {
          setState(res.paid ? 'success' : 'failed');
          setTimeout(() => navigate('/customer/wallet', { replace: true }), 3000);
        })
        .catch(() => {
          setState('failed');
          setTimeout(() => navigate('/customer/wallet', { replace: true }), 3000);
        });
    }
  }, []); // eslint-disable-line

  const SCREENS = {
    processing: { icon: <Spinner />, title: 'جاري التحقق من الدفع...', sub: 'يُرجى الانتظار', color: '#2563eb' },
    verifying:  { icon: <Spinner />, title: 'جاري التحقق...',          sub: 'يُرجى الانتظار', color: '#2563eb' },
    success:    {
      icon: <div style={{ width: 80, height: 80, background: '#dcfce7', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto' }}><CheckCircle2 size={40} color="#16a34a" strokeWidth={2}/></div>,
      title: 'تمت الدفعة بنجاح!', sub: 'يمكنك العودة للتطبيق', color: '#16a34a',
    },
    failed: {
      icon: <div style={{ width: 80, height: 80, background: '#fee2e2', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto' }}><XCircle size={40} color="#dc2626" strokeWidth={2}/></div>,
      title: 'فشلت عملية الدفع', sub: 'لم يتم خصم أي مبلغ', color: '#dc2626',
    },
    cancelled: {
      icon: <div style={{ width: 80, height: 80, background: '#fef3c7', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto' }}><AlertTriangle size={40} color="#d97706" strokeWidth={2}/></div>,
      title: 'تم إلغاء العملية', sub: 'لم يتم خصم أي مبلغ', color: '#d97706',
    },
  };

  const screen = SCREENS[state] || SCREENS.processing;

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc', direction: 'rtl', padding: 24 }}>
      <div style={{ textAlign: 'center', background: '#fff', borderRadius: 24, padding: '40px 32px', boxShadow: '0 4px 32px rgba(0,0,0,0.08)', width: '100%', maxWidth: 320 }}>
        <p style={{ fontWeight: 900, fontSize: 20, color: '#0f172a', marginBottom: 4 }}>وترلي</p>
        <div style={{ margin: '20px 0' }}>{screen.icon}</div>
        <p style={{ fontWeight: 800, fontSize: 17, color: screen.color, marginBottom: 6 }}>{screen.title}</p>
        <p style={{ fontSize: 13, color: '#64748b' }}>{screen.sub}</p>
        {state !== 'processing' && state !== 'verifying' && (
          <button
            onClick={() => navigate('/customer/wallet', { replace: true })}
            style={{ marginTop: 24, width: '100%', background: '#0f4c81', color: '#fff', border: 'none', borderRadius: 14, padding: '12px 0', fontWeight: 700, fontSize: 14, cursor: 'pointer' }}
          >
            العودة للمحفظة
          </button>
        )}
      </div>
    </div>
  );
}

function Spinner() {
  return (
    <div style={{ display: 'flex', justifyContent: 'center', margin: '20px 0' }}>
      <div style={{ width: 48, height: 48, border: '4px solid #e2e8f0', borderTop: '4px solid #0f4c81', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
